/**
 * useRetry Hook
 * Provides retry logic with exponential backoff for failed operations
 */

import { useState, useCallback } from 'react';

interface UseRetryOptions {
  maxRetries?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
}

interface UseRetryReturn {
  retry: <T>(operation: () => Promise<T>) => Promise<T>;
  isRetrying: boolean;
  retryCount: number;
  reset: () => void;
}

/**
 * Hook for retrying failed operations with exponential backoff
 *
 * @param options - Configuration options
 * @returns retry function, retry state, and reset function
 *
 * @example
 * const { retry, isRetrying, retryCount } = useRetry();
 *
 * try {
 *   await retry(() => api.fetchData());
 * } catch (error) {
 *   // Handle error after max retries
 * }
 */
export function useRetry(options: UseRetryOptions = {}): UseRetryReturn {
  const {
    maxRetries = 3,
    initialDelayMs = 1000,
    maxDelayMs = 10000,
  } = options;

  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  /**
   * Reset retry state
   */
  const reset = useCallback(() => {
    setIsRetrying(false);
    setRetryCount(0);
  }, []);

  /**
   * Calculate exponential backoff delay
   */
  const getBackoffDelay = useCallback((attempt: number): number => {
    // Exponential backoff: delay = initialDelay * 2^attempt
    const exponentialDelay = initialDelayMs * Math.pow(2, attempt);

    // Add jitter (random 0-20% variation) to prevent thundering herd
    const jitter = exponentialDelay * 0.2 * Math.random();

    // Cap at max delay
    return Math.min(exponentialDelay + jitter, maxDelayMs);
  }, [initialDelayMs, maxDelayMs]);

  /**
   * Wait for specified delay
   */
  const wait = useCallback((ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
  }, []);

  /**
   * Execute operation with retry logic
   */
  const retry = useCallback(async <T>(operation: () => Promise<T>): Promise<T> => {
    setIsRetrying(true);
    let lastError: Error | null = null;
    let attempt = 0;

    while (attempt <= maxRetries) {
      try {
        // Try the operation
        const result = await operation();

        // Success - reset state and return
        setIsRetrying(false);
        setRetryCount(0);
        return result;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');
        attempt++;
        setRetryCount(attempt);

        // If we've exhausted retries, throw the error
        if (attempt > maxRetries) {
          setIsRetrying(false);
          throw lastError;
        }

        // Wait with exponential backoff before retrying
        const delay = getBackoffDelay(attempt - 1);
        await wait(delay);
      }
    }

    // This shouldn't be reached, but TypeScript needs it
    setIsRetrying(false);
    throw lastError || new Error('Retry failed');
  }, [maxRetries, getBackoffDelay, wait]);

  return {
    retry,
    isRetrying,
    retryCount,
    reset,
  };
}
